// Chris Torrence, 2022
import {
  passSpeedMode,
  passAppleCommandKeyPress,
  passAppleCommandKeyRelease,
  doOnMessage,
  setMain2Worker,
  handleGetMemSize,
  handleGetMachineName,
  handleGetSlotConfig,
  passSetRunMode,
  setBootCallback
} from "./main2worker"
import Apple2Canvas from "./canvas"
import ControlPanel from "./controls/controlpanel"
import { useEffect, useState } from "react"
import DebugSection from "./panels/panels"
import FileInput from "./fileinput"
import { RestoreSaveState } from "./savestate"
import { handleFragment, handleInputParams } from "./inputparams"
import { loadPreferences } from "./localstorage"
import { RUN_MODE, TEST_DEBUG } from "../common/utility"
import DiskCollectionPanel from "./diskdialog/diskcollectionpanel"
import { handleSetTheme, hasDialogMessageContext } from "./ui_utilities"
import DiskInterface from "./devices/disk/diskinterface"
import TouchJoystick from "./controls/touchjoystick"
import { getTheme, isEmbedMode, isGameMode, isMinimalTheme, setHelpText, getColorMode } from "./ui_settings"
import { messagelistener } from "./api/messagelistener"
import { CRTStartup } from "./graphics"
import { startRemoteControlBridge } from "./api/remotecontrol"
import RetroMenuRenderer from "./retro/retromenurenderer"
import { DialogMessage } from "./dialog_message"

const isHeadlessRemoteControl = () => {
  try {
    return new URLSearchParams(window.location.search).get("remoteControlMinimal") === "1"
  } catch {
    return false
  }
}

const DisplayApple2 = () => {
  const [myInit, setMyInit] = useState(false)
  const [renderCount, setRenderCount] = useState(0)
  const [currentSpeed, setCurrentSpeed] = useState(1.02)
  const [avgFPS, setAvgFPS] = useState("")
  const [ctrlKeyMode, setCtrlKeyMode] = useState(0)
  const [openAppleKeyMode, setOpenAppleKeyMode] = useState(0)
  const [closedAppleKeyMode, setClosedAppleKeyMode] = useState(0)
  const [showFileOpenDialog, setShowFileOpenDialog] = useState<{ show: boolean; index: number; append?: boolean }>({ show: false, index: 2, append: false })
  const [worker, setWorker] = useState<Worker | null>(null)

  // We need to create our worker here so it has access to our properties
  // such as cpu speed and help text. Otherwise, if the emulator changed
  // those, we would have no way of setting them here and re-rendering.
  if (!worker) {
    const newWorker = new Worker(new URL("../worker/worker2main", import.meta.url),
      { type: "module" })
    newWorker.onmessage = (e: MessageEvent) => {
      const result = doOnMessage(e)
      if (result) {
        updateDisplay(result.speed, result.helptext)
      }
    }
    setWorker(newWorker)
    setMain2Worker(newWorker)
  }

  const updateDisplay: UpdateDisplay = (speed = 0, newhelptext = "") => {
    if (newhelptext.length > 0) {
      setHelpText(newhelptext)
    }
    if (speed && speed !== currentSpeed) {
      setCurrentSpeed(speed)
    }
    // ***** This is critical to make this update be a function.
    // That way React is forced to pass in the actual previous value,
    // rather than a cached value (thru a closure).
    // If you do setRenderCount(renderCount + 1), renderCount will always be
    // zero and NOTHING will update.
    // CT: I tried removing this to improve CPU usage but then the debug
    // panel stopped reliably updating.
    setRenderCount(prevRenderCount => prevRenderCount + 1)
  }

  const checkContentHeight = () => {
    const body = document.body
    const html = document.documentElement
    const contentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight)
    const viewportHeight = window.innerHeight

    if (contentHeight <= viewportHeight) {
      document.body.classList.add("no-scroll")
    } else {
      document.body.classList.remove("no-scroll")
    }
  }

  if (!myInit) {
    setMyInit(true)

    if ("launchQueue" in window) {
      const queue: LaunchQueue = window.launchQueue as LaunchQueue
      queue.setConsumer(async (launchParams: LaunchParams) => {
        const files: FileSystemFileHandle[] = launchParams.files
        if (files && files.length) {
          const fileContents = await (await files[0].getFile()).text()
          RestoreSaveState(fileContents)
        }
      })
    }
    // TODO: It's unclear whether we need to do this preloadAssets() call
    // or whether just having the assets within that file is good enough
    // for the preloading.
    // preloadAssets()
    passSpeedMode(0)
    loadPreferences()
    startRemoteControlBridge()
    const hasBasicProgram = handleInputParams()
    handleFragment(updateDisplay, hasBasicProgram)

    // Set up CRT boot effect callback
    setBootCallback(() => {
      const canvas = document.getElementById("apple2canvas") as HTMLCanvasElement
      if (canvas) {
        const ctx = canvas.getContext("2d")
        if (ctx) {
          const colorMode = getColorMode()
          CRTStartup(ctx, colorMode)
        }
      }
    })

    // Listen for binary data from VS Code extension
    window.addEventListener("message", messagelistener)

    const handleResize = () => {
      checkContentHeight()
      setRenderCount(prevRenderCount => prevRenderCount + 1)
    }
    window.addEventListener("resize", handleResize)

    if (TEST_DEBUG) {
      passSetRunMode(RUN_MODE.NEED_BOOT)
      setTimeout(() => { passSetRunMode(RUN_MODE.NEED_RESET) }, 700)
      // setTimeout(() => { passPasteText("CALL 768\n") }, 1000)
      setTimeout(() => { passSetRunMode(RUN_MODE.PAUSED) }, 1200)
    }
  }

  const handleCtrlDown = (ctrlKeyMode: number) => {
    setCtrlKeyMode(ctrlKeyMode)
  }

  const handleOpenAppleDown = (newMode: number) => {
    // If we're going from 0 to nonzero, send the Open Apple keypress
    if (openAppleKeyMode === 0 && newMode > 0) {
      passAppleCommandKeyPress(true)
    } else if (openAppleKeyMode > 0 && newMode === 0) {
      // Hack: I guess a timeout of 100 ms is enough time for the
      // emulator to finish processing the keypress.
      window.setTimeout(() => passAppleCommandKeyRelease(true), 100)
    }
    setOpenAppleKeyMode(newMode)
  }

  const handleClosedAppleDown = (newMode: number) => {
    // If we're going from 0 to nonzero, send the Closed Apple keypress
    if (closedAppleKeyMode === 0 && newMode > 0) {
      passAppleCommandKeyPress(false)
    } else if (closedAppleKeyMode > 0 && newMode === 0) {
      // Hack: I guess a timeout of 100 ms is enough time for the
      // emulator to finish processing the keypress.
      window.setTimeout(() => passAppleCommandKeyRelease(false), 100)
    }
    setClosedAppleKeyMode(newMode)
  }

  const handleShowFileOpenDialog = (show: boolean, index: number, append = false) => {
    setShowFileOpenDialog({ show, index, append })
  }

  const props: DisplayProps = {
    speed: currentSpeed,
    renderCount: renderCount,
    setAvgFPS: setAvgFPS,
    ctrlKeyMode: ctrlKeyMode,
    openAppleKeyMode: openAppleKeyMode,
    closedAppleKeyMode: closedAppleKeyMode,
    showFileOpenDialog: showFileOpenDialog,
    updateDisplay: updateDisplay,
    handleCtrlDown: handleCtrlDown,
    handleOpenAppleDown: handleOpenAppleDown,
    handleClosedAppleDown: handleClosedAppleDown,
    setShowFileOpenDialog: handleShowFileOpenDialog,
  }

  const theme = getTheme()
  handleSetTheme(theme)

  const isTouchDevice = "ontouchstart" in document.documentElement
  const height = window.innerHeight ? window.innerHeight : (window.outerHeight - 120)
  const width = window.innerWidth ? window.innerWidth : (window.outerWidth - 20)
  const narrow = isTouchDevice || (width < (1.1 * height))
  const isLandscape = isTouchDevice && (width > height)
  useEffect(() => {
    if (isTouchDevice) {
      document.body.style.marginLeft = "0"
      document.body.style.marginRight = "0"
      document.body.style.marginTop = "0"
    }
  }, [isTouchDevice, isLandscape])
  const machineName = handleGetMachineName()
  const slotConfig = handleGetSlotConfig()
  const isApple2Plus = machineName === "APPLE2P"
  const hasAuxCard = !isApple2Plus && (slotConfig[3] === "aux" || slotConfig[3] === "vidhd")
  const mem = isApple2Plus ? 64 : (hasAuxCard ? (handleGetMemSize() + 64) : 64)
  const memSize = (mem > 1100) ? ((mem / 1024).toFixed() + " MB") : (mem + " KB")
  const status = (isTouchDevice && isLandscape) ? <></> : <div className="default-font footer-item" translate="no">
    <>{currentSpeed} MHz, {memSize}, FPS: {avgFPS}</>
  </div>

  if (isEmbedMode() || isHeadlessRemoteControl()) {
    return <Apple2Canvas {...props} />
  }

  return (
    <>
      <div className={narrow ? "flex-column-gap" : "flex-row-gap"} style={{ alignItems: "inherit" }}>
        <div className={isLandscape ? "flex-row" : "flex-column"}>
          <Apple2Canvas {...props} />
          <div className="flex-row-gap flexwrap no-select" style={{ paddingLeft: "2px" }}>
            <ControlPanel {...props} />
            {!isGameMode() && <DiskInterface {...props} />}
          </div>
          {!isLandscape && !isGameMode() && status}
        </div>
        {isLandscape && !isGameMode() && status}
        {narrow && !isMinimalTheme() && !isGameMode() && <div className="divider"></div>}
        {!isGameMode() && <DebugSection updateDisplay={updateDisplay} narrow={narrow} />}
      </div>
      {isMinimalTheme() && <DiskCollectionPanel {...props} />}
      {isMinimalTheme() && <TouchJoystick />}
      {/* {isMinimalTheme() && isTouchDevice && <TouchJoystick />} */}
      <FileInput {...props} />
      <RetroMenuRenderer displayProps={props} />
      {hasDialogMessageContext() && <DialogMessage updateDisplay={updateDisplay} />}
    </>
  )
}

export default DisplayApple2
